import { useContext, useState } from 'react';
import { useQuery } from '@apollo/client';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import moment from 'moment';
import { GET_PENDING_PACKAGE_CHANGES } from '../../../Querys/prestaciones/querys';
import { AdminContext } from '../../../context/AdminContext/AdminContext';
import { SolicitudCambioPaquete } from '../../../interfaces/Prestaciones.interface';
import {
  PACKAGE_APPROVE_ACTION,
  PackageApproveAction,
  PACKAGE_REQUEST_POLLING_MS,
} from './helpers/packageRequestStatus';
import AprobarSolicitudPaqueteModal from './AprobarSolicitudPaqueteModal';
import '../css/Prestaciones.css';

const formatFecha = (fecha: string) => {
  const num = Number(fecha);
  const m = !isNaN(num) && fecha?.trim() !== '' ? moment.utc(num) : moment.utc(fecha);
  return m.isValid() ? m.format('DD MMM YYYY, HH:mm') : '—';
};

const PackageRequestsContent = () => {
  const { adminState } = useContext(AdminContext);
  const idAprobador = adminState?.id ?? '';

  const [target, setTarget] = useState<{ solicitud: SolicitudCambioPaquete; action: PackageApproveAction } | null>(null);

  const { data, loading, error } = useQuery(GET_PENDING_PACKAGE_CHANGES, {
    fetchPolicy: 'cache-and-network',
    pollInterval: PACKAGE_REQUEST_POLLING_MS,
  });

  const solicitudes: SolicitudCambioPaquete[] = data?.GET_PENDING_PACKAGE_CHANGES ?? [];

  const openAction = (solicitud: SolicitudCambioPaquete, action: PackageApproveAction) =>
    setTarget({ solicitud, action });

  return (
    <Box p={3}>
      <h2 style={{ fontFamily: 'Roboto', color: '#222', marginBottom: 16 }}>
        Aprobaciones de paquete
      </h2>

      {loading && !data ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
          <CircularProgress style={{ color: '#FABB00' }} />
        </div>
      ) : error ? (
        <div style={{ padding: 24, color: '#555', fontFamily: 'Roboto' }}>
          No se pudieron cargar las solicitudes. El servicio de backend aún no está disponible.
        </div>
      ) : (
        <TableContainer component={Paper}>
          <Table aria-label="solicitudes de cambio de paquete">
            <TableHead style={{ backgroundColor: '#f5f5f5' }}>
              <TableRow>
                <TableCell style={{ fontWeight: 600 }}>Colaborador</TableCell>
                <TableCell style={{ fontWeight: 600 }}>Paquete actual</TableCell>
                <TableCell style={{ fontWeight: 600 }}>Paquete solicitado</TableCell>
                <TableCell style={{ fontWeight: 600 }}>Motivo</TableCell>
                <TableCell style={{ fontWeight: 600 }}>Solicitante</TableCell>
                <TableCell style={{ fontWeight: 600 }}>Fecha</TableCell>
                <TableCell style={{ fontWeight: 600 }} align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {solicitudes.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center" style={{ color: '#999', padding: 32 }}>
                    No hay solicitudes pendientes de aprobación.
                  </TableCell>
                </TableRow>
              )}
              {solicitudes.map((s) => (
                <TableRow key={s.id} hover>
                  <TableCell>{s.nombreColaborador ?? s.idColaborador}</TableCell>
                  <TableCell>{s.nombrePaqueteAnterior ?? 'Sin paquete'}</TableCell>
                  <TableCell>{s.nombrePaqueteNuevo ?? s.idPaqueteNuevo}</TableCell>
                  <TableCell style={{ maxWidth: 240, whiteSpace: 'normal' }}>{s.motivo}</TableCell>
                  <TableCell>{s.nombreSolicitante ?? s.idSolicitante}</TableCell>
                  <TableCell>{formatFecha(s.fechaSolicitud)}</TableCell>
                  <TableCell align="center">
                    <Button
                      size="small"
                      onClick={() => openAction(s, PACKAGE_APPROVE_ACTION.APPROVE)}
                      style={{ color: '#11ca73', fontWeight: 600, textTransform: 'none' }}
                    >
                      Aprobar
                    </Button>
                    <Button
                      size="small"
                      onClick={() => openAction(s, PACKAGE_APPROVE_ACTION.REJECT)}
                      style={{ color: '#e53935', fontWeight: 600, textTransform: 'none' }}
                    >
                      Rechazar
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <AprobarSolicitudPaqueteModal
        open={target !== null}
        onClose={() => setTarget(null)}
        solicitud={target?.solicitud ?? null}
        action={target?.action ?? PACKAGE_APPROVE_ACTION.APPROVE}
        idAprobador={idAprobador}
      />
    </Box>
  );
};

export default PackageRequestsContent;
